import { useContext, useMemo } from 'react';

import ProjectContext from '../contexts/project-context';
import { UserContext } from '../contexts/user-context';
import { UsersRoleDto } from '../generated/tellus';

const DEFAULT_COMPANY = 'METRON';
export type Policy = string[];

export interface PolicyContext {
  companyKey?: string | null;
  projectKey?: string | null;
  projectReference?: string | null;
}

export const isSystemRole = (role: string): boolean =>
  role.startsWith('system.');

export const isCompanyRole = (role: string): boolean =>
  role.startsWith('company.');

export const isProjectRole = (role: string): boolean =>
  role.startsWith('project.');

/**
 * Standalone function to check if a policy is satisfied
 * Can be used outside React components
 *
 * @param policy - List of role identifiers that satisfy the policy. If undefined, all users are allowed.
 * @param userRoles - User's roles with scope information
 * @param context - Company and project context for scope validation
 * @returns true if the policy is undefined, or if any policy role is satisfied in the given context
 */
export const checkPolicy = (
  policy?: Policy,
  userRoles: UsersRoleDto[] = [],
  context: PolicyContext = {}
): boolean => {
  if (policy == null) {
    return true;
  }

  const {
    companyKey = DEFAULT_COMPANY,
    projectKey,
    projectReference,
  } = context;

  // Check if any of the policy roles is satisfied by the user's roles
  return policy.some((policyRole) => {
    return userRoles.some((userRole) => {
      if (userRole.role !== policyRole) {
        return false;
      }

      if (isSystemRole(policyRole)) {
        return true; // System roles bypass scope checks
      }

      // Company roles must have matching company scope
      if (isCompanyRole(policyRole)) {
        return userRole.scope === companyKey;
      }

      // Project roles must have matching project scope
      if (isProjectRole(policyRole)) {
        const projectScope = projectKey ?? `${companyKey}-${projectReference}`;
        return userRole.scope === projectScope;
      }

      return false;
    });
  });
};

/**
 * React hook to check if a policy is satisfied in the current user context
 * Automatically retrieves user, company, and project information from React context
 */
export const useCheckPolicy = (policy?: Policy): boolean => {
  const user = useContext(UserContext);
  const projectContext = useContext(ProjectContext);

  return useMemo(() => {
    if (!user || !user.roles) {
      return false;
    }

    const context: PolicyContext = {
      companyKey: user.companyKey,
      projectKey: projectContext?.project?.projectKey,
      projectReference: projectContext?.project?.referenceNumber,
    };

    return checkPolicy(policy, user.roles, context);
  }, [
    policy,
    user,
    projectContext?.project?.projectKey,
    projectContext?.project?.referenceNumber,
  ]);
};

/**
 * Role mapping from old to new system:
 * - system.admin (new) → no old equivalent, bypasses all scope checks
 * - company.admin (scope: company) → SuperAdmin
 * - company.user (scope: company) → Admin
 * - project.user (scope: project) → ContactPerson
 * - project.blaster (scope: project) → ContactPerson (for blast operations)
 * - project.property_owner (scope: project) → ContactPerson (property owner operations)
 *
 * Note: system.admin is included in all policies to bypass scope checks.
 * Company and project roles are only added where they are specifically needed.
 */

export const TellUsAdminPolicy: Policy = ['system.admin'];

export const SuperAdminPolicy: Policy = ['system.admin', 'company.admin'];

export const AdminPolicy: Policy = [
  'system.admin',
  'company.admin',
  'company.user',
];

export const ContactPersonPolicy: Policy = [
  'system.admin',
  'company.admin',
  'company.user',
  'project.user',
  'project.blaster',
  'project.property_owner',
];

export const BlastsViewPolicy: Policy = [
  'system.admin',
  'company.admin',
  'company.user',
  'project.user',
  'project.blaster',
];

export const BlastsManagePolicy: Policy = [
  'system.admin',
  'company.admin',
  'project.blaster',
];

export const MeasuringDataCommentPolicy: Policy = [
  'system.admin',
  'company.admin',
  'company.user',
  'project.user',
  'project.blaster',
];

// Users that are member of the company
export const CompanyMemberPolicy: Policy = ['company.admin', 'company.user'];

export const CompanyUserPolicy: Policy = [
  'system.admin',
  'company.admin',
  'company.user',
];
